/**
 * External dependencies
 */
import clsx from 'clsx';
import type { ElementType } from 'react';

/**
 * WordPress dependencies
 */
import {
	ToggleControl,
	DropZone,
	FlexItem,
	MenuItem,
	VisuallyHidden,
	Placeholder,
	Spinner,
	Button,
	ToggleGroupControl,
	ToggleGroupControlOption,
	UnitControl,
	VStack,
	ItemGroup,
	HStack,
	Truncate,
	type WordPressComponentProps,
	type ItemGroupProps,
} from '@safe-wordpress/components';
import { _x, sprintf } from '@safe-wordpress/i18n';
import { store as noticesStore } from '@safe-wordpress/notices';
import { getFilename } from '@safe-wordpress/url';
import { useRef, useState, useEffect } from '@safe-wordpress/element';
import { useDispatch } from '@safe-wordpress/data';
import { stripHTML, focus } from '@safe-wordpress/dom';
import { isBlobURL } from '@safe-wordpress/blob';
import { uploadMedia, Attachment } from '@safe-wordpress/media-utils';

/**
 * Internal dependencies
 */
import { setImmutably } from '../../utils';
import { hasBackgroundImageValue } from '../global-styles';
import { MediaReplaceFlow } from '../media-replace-flow';
import { FocalPointPicker } from '../../../components';

const IMAGE_BACKGROUND_TYPE = 'image';
const noop = () => {};

/**
 * Get the help text for the background size control.
 *
 * @param {string} value backgroundSize value.
 * @return {string}      Translated help text.
 */
function backgroundSizeHelpText( value: string ) {
	if ( value === 'cover' || value === undefined ) {
		return _x(
			'Image covers the space evenly.',
			'text',
			'nelio-ab-testing'
		);
	}
	if ( value === 'contain' ) {
		return _x(
			'Image is contained without distortion.',
			'text',
			'nelio-ab-testing'
		);
	}
	return _x( 'Image has a fixed width.', 'text', 'nelio-ab-testing' );
}

/**
 * Converts decimal x and y coords from FocalPointPicker to percentage-based values
 * to use as backgroundPosition value.
 *
 * @param {{x?:number, y?:number}} value   FocalPointPicker coords.
 * @param                          value.x
 * @param                          value.y
 * @return {string}      				 backgroundPosition value.
 */
export const coordsToBackgroundPosition = ( value: {
	x?: number;
	y?: number;
} ): string | undefined => {
	if (
		! value ||
		value.x === undefined ||
		value.y === undefined ||
		( isNaN( value.x ) && isNaN( value.y ) )
	) {
		return undefined;
	}

	const x = isNaN( value.x ) ? 0.5 : value.x;
	const y = isNaN( value.y ) ? 0.5 : value.y;

	return `${ x * 100 }% ${ y * 100 }%`;
};

/**
 * Converts backgroundPosition value to x and y coords for FocalPointPicker.
 *
 * @param {string} value backgroundPosition value.
 * @return {{x?:number, y?:number}}       FocalPointPicker coords.
 */
export const backgroundPositionToCoords = (
	value?: string
): { x: number; y: number } => {
	if ( ! value ) {
		return { x: 0.5, y: 0.5 };
	}

	let [ x, y ] = value.split( ' ' ).map( ( v ) => parseFloat( v ) / 100 );
	x = x === undefined || isNaN( x ) ? 0.5 : x;
	y = y === undefined || isNaN( y ) ? x : y;

	return { x, y };
};

type InspectorImagePreviewItemProps = {
	readonly as?: ElementType;
	readonly imgUrl?: string;
	readonly toggleProps?: Omit<
		WordPressComponentProps< ItemGroupProps, 'div' >,
		'children'
	> & { isOpen?: boolean };
	readonly filename?: string;
	readonly label: string;
	readonly className?: string;
	readonly onToggleCallback?: ( isOpen?: boolean ) => void;
};
function InspectorImagePreviewItem( {
	as = 'span',
	imgUrl,
	toggleProps = {},
	filename,
	label,
	className,
	onToggleCallback = noop,
}: InspectorImagePreviewItemProps ): JSX.Element {
	useEffect( () => {
		if ( typeof toggleProps?.isOpen !== 'undefined' ) {
			onToggleCallback( toggleProps?.isOpen );
		}
	}, [ toggleProps?.isOpen, onToggleCallback ] );
	return (
		<ItemGroup as={ as } className={ className } { ...toggleProps }>
			<HStack
				justify="flex-start"
				as="span"
				className="block-editor-global-styles-background-panel__inspector-preview-inner"
			>
				{ imgUrl && (
					<span
						className="block-editor-global-styles-background-panel__inspector-image-indicator-wrapper"
						aria-hidden
					>
						<span
							className="block-editor-global-styles-background-panel__inspector-image-indicator"
							style={ {
								backgroundImage: `url(${ imgUrl })`,
							} }
						/>
					</span>
				) }
				<FlexItem as="span" style={ imgUrl ? {} : { flexGrow: 1 } }>
					<Truncate
						numberOfLines={ 1 }
						className="block-editor-global-styles-background-panel__inspector-media-replace-title"
					>
						{ label }
					</Truncate>
					<VisuallyHidden as="span">
						{ imgUrl
							? sprintf(
									/* translators: %s: File name. */
									_x(
										'Background image: %s',
										'text',
										'nelio-ab-testing'
									),
									filename || label
							  )
							: _x(
									'No background image selected',
									'text',
									'nelio-ab-testing'
							  ) }
					</VisuallyHidden>
				</FlexItem>
			</HStack>
		</ItemGroup>
	);
}

function LoadingSpinner() {
	return (
		<Placeholder className="block-editor-global-styles-background-panel__loading">
			<Spinner />
		</Placeholder>
	);
}

type BackgroundImageControlsProps = {
	readonly onChange: ( value: BackgroundValue ) => void;
	readonly style: BackgroundValue;
	readonly inheritedValue?: BackgroundValue;
	readonly onRemoveImage?: () => void;
	readonly onResetImage?: () => void;
	readonly displayInPanel?: boolean;
	readonly defaultValues?: DefaultValue;
	readonly disabledProps: BackgroundImagePanelProps[ 'disabledProps' ];
};
function BackgroundImageControls( {
	onChange,
	style,
	inheritedValue,
	onRemoveImage = noop,
	onResetImage = noop,
	displayInPanel,
	defaultValues,
	disabledProps,
}: BackgroundImageControlsProps ) {
	const [ isUploading, setIsUploading ] = useState( false );

	const {
		id,
		title,
		url = '',
	} = ( typeof style.background?.backgroundImage !== 'string' &&
		style.background?.backgroundImage ) || {
		...( typeof inheritedValue?.background?.backgroundImage !== 'string' &&
			inheritedValue?.background?.backgroundImage ),
	};
	const replaceContainerRef = useRef< HTMLDivElement | null >( null );
	const { createErrorNotice } = useDispatch( noticesStore );
	const onUploadError = ( message: Error | string ) => {
		const safeMessage = stripHTML(
			'string' === typeof message ? message : message.toString()
		);
		void createErrorNotice( safeMessage, { type: 'snackbar' } );
		setIsUploading( false );
	};

	const resetBackgroundImage = () =>
		onChange(
			setImmutably(
				style,
				[ 'background', 'backgroundImage' ],
				undefined
			)
		);

	const onSelectMedia = ( media: Attachment ) => {
		if ( ! media || ! media.url ) {
			resetBackgroundImage();
			setIsUploading( false );
			return;
		}

		if ( isBlobURL( media.url ) ) {
			setIsUploading( true );
			return;
		}

		// For media selections originated from a file upload.
		if ( media.media_type && media.media_type !== IMAGE_BACKGROUND_TYPE ) {
			onUploadError(
				_x(
					'Only images can be used as a background image.',
					'text',
					'nelio-ab-testing'
				)
			);
			return;
		}

		const sizeValue =
			style?.background?.backgroundSize || defaultValues?.backgroundSize;
		const positionValue = style?.background?.backgroundPosition;
		onChange(
			setImmutably( style, [ 'background' ], {
				...style?.background,
				backgroundImage: {
					url: media.url,
					id: media.id,
					source: 'file',
					title: media.title || undefined,
				},
				backgroundPosition:
					/*
					 * A background image uploaded and set in the editor receives a default background position of '50% 0',
					 * when the background image size is the equivalent of "Tile".
					 * This is to increase the chance that the image's focus point is visible.
					 * This is in-editor only to assist with the user experience.
					 */
					! positionValue && ( 'auto' === sizeValue || ! sizeValue )
						? '50% 0'
						: positionValue,
				backgroundSize: sizeValue,
			} )
		);
		setIsUploading( false );
	};

	// Drag and drop callback, restricting image to one.
	const onFilesDrop = ( filesList: File[] ) => {
		uploadMedia( {
			allowedTypes: [ IMAGE_BACKGROUND_TYPE ],
			filesList,
			onFileChange( [ image ] ) {
				onSelectMedia( image as Attachment );
			},
			onError: onUploadError,
		} );
	};

	const hasValue = hasBackgroundImageValue( style );

	const closeAndFocus = () => {
		const [ toggleButton ] = focus.tabbable.find(
			replaceContainerRef.current as HTMLElement
		);
		// Focus the toggle button and close the dropdown menu.
		// This ensures similar behaviour as to selecting an image, where the dropdown is
		// closed and focus is redirected to the dropdown toggle button.
		toggleButton?.focus();
		toggleButton?.click();
	};

	const onRemove = () =>
		onChange(
			setImmutably( style, [ 'background' ], {
				backgroundImage: 'none',
			} )
		);
	const canRemove = ! hasValue && hasBackgroundImageValue( inheritedValue );
	const imgLabel =
		title ||
		getFilename( url ) ||
		_x( 'Add background image', 'command', 'nelio-ab-testing' );

	if ( disabledProps.includes( 'backgroundImage' ) ) {
		return null;
	}

	return (
		<div
			ref={ replaceContainerRef }
			className="block-editor-global-styles-background-panel__image-tools-panel-item"
		>
			{ isUploading && <LoadingSpinner /> }
			<MediaReplaceFlow
				mediaId={ id as number }
				mediaURL={ url }
				allowedTypes={ [ IMAGE_BACKGROUND_TYPE ] }
				accept="image/*"
				onSelect={ onSelectMedia }
				popoverProps={ {
					className: clsx( {
						'block-editor-global-styles-background-panel__media-replace-popover':
							displayInPanel,
					} ),
				} }
				name={
					<InspectorImagePreviewItem
						className="block-editor-global-styles-background-panel__image-preview"
						imgUrl={ url }
						filename={ title }
						label={ imgLabel }
					/>
				}
				renderToggle={ ( props ) => (
					<Button { ...props } __next40pxDefaultSize />
				) }
				onError={ onUploadError }
				onReset={ () => {
					closeAndFocus();
					onResetImage();
				} }
			>
				{ canRemove && (
					<MenuItem
						role="menuitem"
						onClick={ () => {
							closeAndFocus();
							onRemove();
							onRemoveImage();
						} }
					>
						{ _x( 'Remove', 'command', 'nelio-ab-testing' ) }
					</MenuItem>
				) }
			</MediaReplaceFlow>
			<DropZone
				onFilesDrop={ onFilesDrop }
				label={ _x( 'Drop to upload', 'command', 'nelio-ab-testing' ) }
			/>
		</div>
	);
}

type BackgroundSizeControlsProps = {
	readonly onChange: ( value: BackgroundValue ) => void;
	readonly style?: BackgroundValue;
	readonly inheritedValue?: BackgroundValue;
	readonly defaultValues?: DefaultValue;
	readonly disabledProps: BackgroundImagePanelProps[ 'disabledProps' ];
};
function BackgroundSizeControls( {
	onChange,
	style,
	inheritedValue,
	defaultValues,
	disabledProps,
}: BackgroundSizeControlsProps ) {
	const sizeValue =
		style?.background?.backgroundSize ||
		inheritedValue?.background?.backgroundSize ||
		'none';
	const repeatValue =
		style?.background?.backgroundRepeat ||
		inheritedValue?.background?.backgroundRepeat;
	const imageValue =
		/* eslint-disable no-nested-ternary */
		typeof style?.background?.backgroundImage === 'string'
			? style?.background?.backgroundImage
			: typeof style?.background?.backgroundImage !== 'string' &&
			  typeof style?.background?.backgroundImage?.url !== 'undefined'
			? style?.background?.backgroundImage?.url
			: typeof inheritedValue?.background?.backgroundImage === 'string'
			? inheritedValue?.background?.backgroundImage
			: typeof inheritedValue?.background?.backgroundImage !== 'string' &&
			  typeof inheritedValue?.background?.backgroundImage !== 'undefined'
			? inheritedValue?.background?.backgroundImage?.url ?? ''
			: '';
	/* eslint-enable no-nested-ternary */
	const isUploadedImage =
		typeof style?.background?.backgroundImage !== 'string' &&
		style?.background?.backgroundImage?.id;
	const positionValue =
		style?.background?.backgroundPosition ||
		inheritedValue?.background?.backgroundPosition;
	const attachmentValue =
		style?.background?.backgroundAttachment ||
		inheritedValue?.background?.backgroundAttachment;

	/*
	 * Set default values for uploaded images.
	 * The default values are passed by the consumer.
	 * Block-level controls may have different defaults to root-level controls.
	 * A falsy value is treated by default as `auto` (Tile).
	 */
	let currentValueForToggle =
		! sizeValue && isUploadedImage
			? defaultValues?.backgroundSize
			: sizeValue || 'auto';
	/*
	 * The incoming value could be a value + unit, e.g. '20px'.
	 * In this case set the value to 'tile'.
	 */
	currentValueForToggle = ! [ 'cover', 'contain', 'auto' ].includes(
		currentValueForToggle ?? 'auto'
	)
		? 'auto'
		: currentValueForToggle;
	/*
	 * If the current value is `cover` and the repeat value is `undefined`, then
	 * the toggle should be unchecked as the default state. Otherwise, the toggle
	 * should reflect the current repeat value.
	 */
	const repeatCheckedValue = ! (
		repeatValue === 'no-repeat' ||
		( currentValueForToggle === 'cover' && repeatValue === undefined )
	);

	const updateBackgroundSize = ( next: string | number | undefined ) => {
		// When switching to 'contain' toggle the repeat off.
		let nextRepeat = repeatValue;
		let nextPosition = positionValue;

		if ( next === 'contain' ) {
			nextRepeat = 'no-repeat';
			nextPosition = undefined;
		}

		if ( next === 'cover' ) {
			nextRepeat = undefined;
			nextPosition = undefined;
		}

		if (
			( currentValueForToggle === 'cover' ||
				currentValueForToggle === 'contain' ) &&
			next === 'auto'
		) {
			nextRepeat = undefined;
			/*
			 * A background image uploaded and set in the editor (an image with a record id),
			 * receives a default background position of '50% 0',
			 * when the toggle switches to "Tile". This is to increase the chance that
			 * the image's focus point is visible.
			 * This is in-editor only to assist with the user experience.
			 */
			if (
				typeof style?.background?.backgroundImage !== 'string' &&
				!! style?.background?.backgroundImage?.id
			) {
				nextPosition = '50% 0';
			}
		}

		/*
		 * Next will be null when the input is cleared,
		 * in which case the value should be 'auto'.
		 */
		if ( ! next && currentValueForToggle === 'auto' ) {
			next = 'auto';
		}

		onChange(
			setImmutably( style as BackgroundValue, [ 'background' ], {
				...style?.background,
				backgroundPosition: nextPosition,
				backgroundRepeat: nextRepeat,
				backgroundSize: next,
			} )
		);
	};

	const updateBackgroundPosition = ( next: { x?: number; y?: number } ) => {
		onChange(
			setImmutably(
				style as BackgroundValue,
				[ 'background', 'backgroundPosition' ],
				coordsToBackgroundPosition( next )
			)
		);
	};

	const toggleIsRepeated = () =>
		onChange(
			setImmutably(
				style as BackgroundValue,
				[ 'background', 'backgroundRepeat' ],
				repeatCheckedValue === true ? 'no-repeat' : 'repeat'
			)
		);

	const toggleScrollWithPage = () =>
		onChange(
			setImmutably(
				style as BackgroundValue,
				[ 'background', 'backgroundAttachment' ],
				attachmentValue === 'fixed' ? 'scroll' : 'fixed'
			)
		);

	// Set a default background position for non-site-wide, uploaded images with a size of 'contain'.
	const backgroundPositionValue =
		! positionValue && isUploadedImage && 'contain' === sizeValue
			? defaultValues?.backgroundPosition
			: positionValue;

	return (
		<VStack spacing={ 3 } className="single-column">
			<FocalPointPicker
				__nextHasNoMarginBottom
				label={ _x( 'Focal point', 'text', 'nelio-ab-testing' ) }
				url={ imageValue }
				value={ backgroundPositionToCoords( backgroundPositionValue ) }
				onChange={ updateBackgroundPosition }
				disablePositionX={ disabledProps.includes(
					'backgroundPositionX'
				) }
				disablePositionY={ disabledProps.includes(
					'backgroundPositionY'
				) }
			/>
			<ToggleControl
				__nextHasNoMarginBottom
				label={ _x( 'Fixed background', 'text', 'nelio-ab-testing' ) }
				checked={ attachmentValue === 'fixed' }
				onChange={ toggleScrollWithPage }
				disabled={ disabledProps.includes( 'backgroundAttachment' ) }
			/>
			<ToggleGroupControl
				__nextHasNoMarginBottom
				size="__unstable-large"
				label={ _x( 'Size', 'text', 'nelio-ab-testing' ) }
				value={ currentValueForToggle }
				onChange={ updateBackgroundSize }
				isBlock
				help={ backgroundSizeHelpText( sizeValue ) }
			>
				<ToggleGroupControlOption
					key="cover"
					value="cover"
					label={ _x(
						'Cover',
						'Size option for background image control'
					) }
					disabled={ disabledProps.includes( 'backgroundSize' ) }
				/>
				<ToggleGroupControlOption
					key="contain"
					value="contain"
					label={ _x(
						'Contain',
						'Size option for background image control'
					) }
					disabled={ disabledProps.includes( 'backgroundSize' ) }
				/>
				<ToggleGroupControlOption
					key="tile"
					value="auto"
					label={ _x(
						'Tile',
						'Size option for background image control'
					) }
					disabled={ disabledProps.includes( 'backgroundSize' ) }
				/>
			</ToggleGroupControl>
			<HStack justify="flex-start" spacing={ 2 } as="span">
				<UnitControl
					aria-label={ _x(
						'Background image width',
						'text',
						'nelio-ab-testing'
					) }
					onChange={ updateBackgroundSize }
					value={ sizeValue }
					size="__unstable-large"
					__unstableInputWidth="100px"
					min={ 0 }
					placeholder={ _x( 'Auto', 'text', 'nelio-ab-testing' ) }
					disabled={
						disabledProps.includes( 'backgroundSize' ) ||
						currentValueForToggle !== 'auto' ||
						currentValueForToggle === undefined
					}
				/>
				<ToggleControl
					__nextHasNoMarginBottom
					label={ _x( 'Repeat', 'text', 'nelio-ab-testing' ) }
					checked={ repeatCheckedValue }
					onChange={ toggleIsRepeated }
					disabled={
						disabledProps.includes( 'backgroundRepeat' ) ||
						currentValueForToggle === 'cover'
					}
				/>
			</HStack>
		</VStack>
	);
}

type DefaultValue = {
	readonly backgroundPosition?: string;
	readonly backgroundSize?: string;
};

type BackgroundValue = {
	readonly background?: {
		readonly backgroundImage?:
			| {
					readonly id?: number;
					readonly title?: string;
					readonly url?: string;
			  }
			| string;
		readonly backgroundPosition?: string;
		readonly backgroundSize?: string;
		readonly backgroundRepeat?: string;
		readonly backgroundAttachment?: string;
	};
};
type BackgroundImagePanelProps = {
	readonly value: BackgroundValue;
	readonly onChange: ( value: BackgroundValue ) => void;
	readonly inheritedValue?: BackgroundValue;
	readonly settings: BackgroundValue;
	readonly disabledProps: ReadonlyArray<
		| 'backgroundImage'
		| 'backgroundAttachment'
		| 'backgroundPositionX'
		| 'backgroundPositionY'
		| 'backgroundRepeat'
		| 'backgroundSize'
	>;
	readonly defaultValues?: DefaultValue;
};

export default function BackgroundImagePanel( {
	value,
	onChange,
	inheritedValue = value,
	settings,
	disabledProps,
	defaultValues = {},
}: BackgroundImagePanelProps ): JSX.Element {
	const resetBackground = () =>
		onChange( setImmutably( value, [ 'background' ], {} ) );

	const hasImageValue = hasBackgroundImageValue( value );
	const imageValue =
		value?.background?.backgroundImage ||
		inheritedValue?.background?.backgroundImage;

	const shouldShowBackgroundImageControls =
		hasImageValue &&
		'none' !== imageValue &&
		( settings?.background?.backgroundSize ||
			settings?.background?.backgroundPosition ||
			settings?.background?.backgroundRepeat );

	const [ isDropDownOpen, setIsDropDownOpen ] = useState( false );

	return (
		<div
			className={ clsx(
				'block-editor-global-styles-background-panel__inspector-media-replace-container',
				{
					'is-open': isDropDownOpen,
				}
			) }
		>
			{ shouldShowBackgroundImageControls ? (
				<div className="block-editor-color-gradient-control__panel">
					<VStack spacing={ 3 } className="single-column">
						<BackgroundImageControls
							onChange={ onChange }
							style={ value }
							inheritedValue={ inheritedValue }
							displayInPanel
							onResetImage={ () => {
								setIsDropDownOpen( false );
								resetBackground();
							} }
							onRemoveImage={ () => setIsDropDownOpen( false ) }
							defaultValues={ defaultValues }
							disabledProps={ disabledProps }
						/>
						<BackgroundSizeControls
							onChange={ onChange }
							style={ value }
							defaultValues={ defaultValues }
							inheritedValue={ inheritedValue }
							disabledProps={ disabledProps }
						/>
					</VStack>
				</div>
			) : (
				<BackgroundImageControls
					onChange={ onChange }
					style={ value }
					inheritedValue={ inheritedValue }
					defaultValues={ defaultValues }
					onResetImage={ () => {
						setIsDropDownOpen( false );
						resetBackground();
					} }
					onRemoveImage={ () => setIsDropDownOpen( false ) }
					disabledProps={ disabledProps }
				/>
			) }
		</div>
	);
}
